// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useRef, useState } from 'react';
import type { WebAnalyticsApi } from '../api';
import { errorMessage } from '../api';
import type { Block, BlockCreate, BlockDuration, BlockKind } from '../types';
import { BusyGem, Button, ErrorLine, Field, Modal, Note, OptionPills, TextInput } from '../ui';

const KINDS: Array<{ id: BlockKind; label: string }> = [
  { id: 'ip', label: 'IP' }, { id: 'net24', label: 'IP range' }, { id: 'country', label: 'Country' }, { id: 'bot', label: 'Bot' },
];
const DURATIONS: Array<{ id: BlockDuration; label: string }> = [
  { id: '1h', label: '1 hour' }, { id: '24h', label: '24 hours' }, { id: '7d', label: '7 days' }, { id: '30d', label: '30 days' }, { id: 'permanent', label: 'Permanent' },
];
export const DURATION_LABEL: Record<BlockDuration, string> = Object.fromEntries(DURATIONS.map((d) => [d.id, d.label])) as Record<BlockDuration, string>;

/**
 * Block traffic. POST body { kind, target, duration, scope, reason }.
 * Admin on `_all` → only "All RubyTill websites"; admin on a site → that site or all;
 * tenant → its own website only (the 'all' option is not offered at all).
 * Server messages (country refused for scope all, quota 409, already blocked) are shown verbatim.
 */
export const BlockModal: React.FC<{
  api: WebAnalyticsApi;
  mode: 'admin' | 'tenant';
  siteId: string;
  siteName: string;
  prefill?: { kind?: BlockKind; target?: string };
  onClose: () => void;
  onCreated: (b: Block, body: BlockCreate) => void;
}> = ({ api, mode, siteId, siteName, prefill, onClose, onCreated }) => {
  const admin = mode === 'admin';
  const onAll = siteId === '_all';
  const [kind, setKind] = useState<BlockKind>(prefill?.kind || 'ip');
  const [target, setTarget] = useState(prefill?.target || '');
  const [scope, setScope] = useState<'site' | 'all'>(admin && onAll ? 'all' : 'site');
  const [duration, setDuration] = useState<BlockDuration>('7d');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const targetRef = useRef<HTMLInputElement>(null);

  const scopeOptions: Array<{ id: 'site' | 'all'; label: string; disabled?: boolean }> = admin
    ? [{ id: 'site', label: onAll ? 'One website' : siteName, disabled: onAll }, { id: 'all', label: 'All RubyTill websites' }]
    : [{ id: 'site', label: 'My website' }];

  const submit = async () => {
    const t = target.trim();
    if (!t) { setInvalid(true); targetRef.current?.focus(); return; }
    const body: BlockCreate = { kind, target: t, duration, scope, reason: reason.trim() };
    setBusy(true); setError(null);
    try {
      const b = await api.createBlock(siteId, body);
      onCreated(b, body);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Block traffic" onClose={onClose} testId="wa-block-modal"
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button tone="ruby" onClick={submit} disabled={busy} data-testid="wa-block-submit">{busy ? <BusyGem /> : 'Block'}</Button>
      </>}
    >
      <Field label="Type"><OptionPills<BlockKind> value={kind} options={KINDS} onChange={setKind} testId="wa-block-kind" /></Field>
      <Field label={kind === 'country' ? 'Country (two-letter code)' : kind === 'bot' ? 'Bot name or user agent' : kind === 'net24' ? 'Address range' : 'Address'}>
        <TextInput
          ref={targetRef} value={target} invalid={invalid} data-testid="wa-block-target"
          onChange={(e) => { setTarget(e.target.value); setInvalid(false); }}
          placeholder={kind === 'country' ? 'e.g. RU' : kind === 'bot' ? 'e.g. MJ12bot' : kind === 'net24' ? 'e.g. 185.220.101.0/24' : 'e.g. 45.141.84.17'}
        />
      </Field>
      <Field label="Applies to">
        <OptionPills<"site" | "all"> value={scope} options={scopeOptions} onChange={setScope} testId="wa-block-scope" />
        {!admin && <Note>Only RubyTill can block across every website.</Note>}
      </Field>
      <Field label="For how long"><OptionPills<BlockDuration> value={duration} options={DURATIONS} onChange={setDuration} testId="wa-block-duration" /></Field>
      <Field label="Reason (shown in the audit log)">
        <TextInput value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Card testing at checkout" data-testid="wa-block-reason" />
      </Field>
      {error && <ErrorLine message={error} />}
      <Note>Takes effect within a few minutes. Every block and unblock is recorded in the audit log.</Note>
    </Modal>
  );
};

export default BlockModal;
